import cn from 'classnames'
import type { ReactNode } from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Loader } from 'core-components/library/loader'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { getCockpitFiscalYearLabel } from '../../../../lib/getCockpitFiscalYearLabel'
import { getOfferingTitleKey } from '../../../../lib/getOfferingTitleKey'
import {
  selectCockpitOffering,
  selectCockpitSwitchingFYear,
  selectCockpitSwitchingOnOffering,
} from '../../../../model/selectors'

import styles from './CockpitBoardSlide.module.scss'

/** The H2 review plan cards' deck motion (`PlanCardGroup`): the old board tips up and away while
 *  the new one rises from below. */
const SWITCH_DURATION_MS = 360
const SWITCH_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)'
const LOWERED = 'translateY(44px) rotateX(-8deg) scale(0.985)'
const RAISED = 'translateY(-44px) rotateX(8deg) scale(0.985)'
const AT_REST = 'translateY(0) rotateX(0deg) scale(1)'

/** The `data-tour-id`s the cockpit's tour marks the board's tiles with: on the board, and on the
 *  copy a maximized tile lays over the whole board. Each cockpit passes its own. */
export type TCockpitBoardTileTargets = Record<
  | 'tileRevenue'
  | 'tileInitiatives'
  | 'tileAttention'
  | 'tileDocuments'
  | 'tileRevenueExpanded'
  | 'tileInitiativesExpanded'
  | 'tileAttentionExpanded'
  | 'tileDocumentsExpanded',
  string
>

const toSelector = (ids: string[]) => ids.map((id) => `[data-tour-id="${id}"]`).join(', ')

interface ITileFoot {
  top: number
  left: number
  width: number
  height: number
}

const isSameFeet = (left: ITileFoot[], right: ITileFoot[]) =>
  left.length === right.length &&
  left.every(
    (foot, index) =>
      foot.top === right[index].top &&
      foot.left === right[index].left &&
      foot.width === right[index].width &&
      foot.height === right[index].height
  )

/** Over the middle of the board while its data is fetched again with the board kept on screen.
 *  It stands outside the viewport, whose changes the deck measures its sheets on. */
const ReloadOverlay = ({ title, hint }: { title: ReactNode; hint: ReactNode }) => (
  <div className={styles.reloadOverlay} role="status">
    <div className={styles.reloadBox}>
      <Loader dimension={{ width: 40, height: 40 }} />
      <span className={styles.reloadTitle}>{title}</span>
      <span className={styles.reloadHint}>{hint}</span>
    </div>
  </div>
)

const SwitchingOnOverlay = ({ offering }: { offering: string }) => {
  const t = useSafetyTranslation()

  return (
    <ReloadOverlay
      title={
        <Translation
          span
          text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.programTypes.switchingOn}
          replaceParams={[{ key: '{offering}', value: t(getOfferingTitleKey(offering)) }]}
        />
      }
      hint={
        <Translation span text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.programTypes.switchingOnHint} />
      }
    />
  )
}

const SwitchingFYearOverlay = ({ fYear }: { fYear: number }) => (
  <ReloadOverlay
    title={
      <Translation
        span
        text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.scopeCard.loadingYear}
        replaceParams={[{ key: '{year}', value: getCockpitFiscalYearLabel(fYear) }]}
      />
    }
    hint={<Translation span text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.scopeCard.loadingYearHint} />}
  />
)

/** The boxes of the tiles standing on the board's foot, in the viewport's coordinates: the deck's
 *  sheets lie under each of them, so the gaps between the tiles run on through the deck. */
const useTileFeet = (
  viewportRef: React.RefObject<HTMLDivElement | null>,
  tileTargets: TCockpitBoardTileTargets
) => {
  const [feet, setFeet] = useState<ITileFoot[]>([])
  const tileSelector = toSelector([
    tileTargets.tileRevenue,
    tileTargets.tileInitiatives,
    tileTargets.tileAttention,
    tileTargets.tileDocuments,
  ])
  const expandedTileSelector = toSelector([
    tileTargets.tileRevenueExpanded,
    tileTargets.tileInitiativesExpanded,
    tileTargets.tileAttentionExpanded,
    tileTargets.tileDocumentsExpanded,
  ])

  useLayoutEffect(() => {
    const viewport = viewportRef.current

    if (!viewport) {
      return
    }

    const measure = () => {
      const expanded = [...viewport.querySelectorAll<HTMLElement>(expandedTileSelector)]
      const tiles = expanded.length
        ? expanded
        : [...viewport.querySelectorAll<HTMLElement>(tileSelector)].filter(
            (tile) => !tile.closest('[aria-hidden="true"]')
          )
      const origin = viewport.getBoundingClientRect()
      const boxes = tiles.map((tile) => tile.getBoundingClientRect())
      const foot = Math.max(...boxes.map((box) => box.bottom))
      const next = boxes
        .filter((box) => box.width > 0 && Math.abs(box.bottom - foot) < 2)
        .map((box) => ({
          top: box.top - origin.top,
          left: box.left - origin.left,
          width: box.width,
          height: box.height,
        }))

      /* The observers fire on every change under the viewport, the board's own renders included:
         an unchanged measure keeps the state as it is, or a render would set off the next one. */
      setFeet((current) => (isSameFeet(current, next) ? current : next))
    }

    measure()

    const observer = new ResizeObserver(measure)
    const mutations = new MutationObserver(measure)

    observer.observe(viewport)
    viewport.querySelectorAll(tileSelector).forEach((tile) => observer.observe(tile))
    mutations.observe(viewport, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      mutations.disconnect()
    }
  }, [expandedTileSelector, tileSelector, viewportRef])

  return feet
}

interface ICockpitBoardSlideProps {
  tileTargets: TCockpitBoardTileTargets
  /** The board takes the height it is given instead of its content's: the manager cockpit fits
   *  its board to the screen. */
  fill?: boolean
  children: ReactNode
}

/** The board of the selected programme, lying on a deck of the other programmes' boards — two
 *  sheets under each tile on its foot, as the H2 review plan cards. Picking another tab deals the next board:
 *  a still copy of the old one tips up and away while the new one rises in its place. While a
 *  programme is switched on or another year loads, the board is blurred under one loader until its
 *  data is back. */
export const CockpitBoardSlide = ({
  tileTargets,
  fill = false,
  children,
}: ICockpitBoardSlideProps) => {
  const viewportRef = useRef<HTMLDivElement>(null)
  const boardRef = useRef<HTMLDivElement>(null)
  const selectedOffering = useSelector(selectCockpitOffering)
  const switchingOnOffering = useSelector(selectCockpitSwitchingOnOffering)
  const switchingFYear = useSelector(selectCockpitSwitchingFYear)
  const isReloading = Boolean(switchingOnOffering) || switchingFYear !== null
  const shownOfferingRef = useRef(selectedOffering)
  const leavingRef = useRef<HTMLElement | null>(null)
  const outgoingRef = useRef<HTMLElement | null>(null)
  const feet = useTileFeet(viewportRef, tileTargets)

  /* Taken while rendering, before the new programme reaches the page: the copy is what leaves. */
  if (shownOfferingRef.current !== selectedOffering && !outgoingRef.current && boardRef.current) {
    outgoingRef.current = boardRef.current.cloneNode(true) as HTMLElement
  }

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const board = boardRef.current
    const outgoing = outgoingRef.current

    if (shownOfferingRef.current === selectedOffering) {
      return
    }

    shownOfferingRef.current = selectedOffering
    outgoingRef.current = null

    if (
      !viewport ||
      !board ||
      !outgoing ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return
    }

    leavingRef.current?.remove()
    leavingRef.current = outgoing

    outgoing.classList.add(styles.leaving)
    outgoing.setAttribute('aria-hidden', 'true')
    outgoing.inert = true
    outgoing.querySelectorAll('[id], [data-tour-id]').forEach((node) => {
      node.removeAttribute('id')
      node.removeAttribute('data-tour-id')
    })
    viewport.appendChild(outgoing)
    viewport.classList.add(styles.dealing)

    const exit = outgoing.animate(
      [
        { opacity: 1, transform: AT_REST },
        { opacity: 0, transform: RAISED },
      ],
      { duration: SWITCH_DURATION_MS, easing: SWITCH_EASING, fill: 'forwards' }
    )

    board.animate(
      [
        { opacity: 0, transform: LOWERED },
        { opacity: 1, transform: AT_REST },
      ],
      { duration: SWITCH_DURATION_MS, easing: SWITCH_EASING }
    )

    const settle = () => {
      outgoing.remove()

      if (leavingRef.current === outgoing) {
        leavingRef.current = null
        viewport.classList.remove(styles.dealing)
      }
    }

    exit.onfinish = settle
    exit.oncancel = settle
  }, [selectedOffering])

  useEffect(() => () => leavingRef.current?.remove(), [])

  return (
    <div className={cn(styles.deck, { [styles.fill]: fill })}>
      <div ref={viewportRef} className={styles.viewport}>
        {feet.map((foot) => (
          <span
            key={`${foot.left}-${foot.width}`}
            className={styles.sheets}
            style={{ top: foot.top, left: foot.left, width: foot.width, height: foot.height }}
            aria-hidden="true"
          />
        ))}
        <div
          ref={boardRef}
          className={cn(styles.board, { [styles.reloading]: isReloading })}
          inert={isReloading}
        >
          {children}
        </div>
      </div>

      {/* The board stays where it is while a programme is switched on: its figures are fetched
          again under it, with the work said once over the middle of the board. */}
      {switchingOnOffering ? (
        <SwitchingOnOverlay offering={switchingOnOffering} />
      ) : (
        switchingFYear !== null && <SwitchingFYearOverlay fYear={switchingFYear} />
      )}
    </div>
  )
}

CockpitBoardSlide.displayName = 'CockpitBoardSlide'
